/**
 * ALERON search overlay.
 *
 * A full-screen white panel with one large input. Results come from Shopify's
 * Predictive Search API and render as image tiles while typing.
 *
 * Requests are debounced and the previous one is aborted, so a fast typist
 * never pays for intermediate queries and a slow response can never overwrite
 * the results of a newer one.
 */

import { AleronOverlay, define } from '@theme/aleron-overlay';
import { formatMoneyFromDecimal } from '@theme/aleron-money';

const DEBOUNCE_MS = 200;
const MIN_LENGTH = 2;

class AleronSearch extends AleronOverlay {
  #controller = null;
  #timer = null;

  connectedCallback() {
    super.connectedCallback();

    this.input = this.querySelector('[data-aleron-search-input]');
    this.results = this.querySelector('[data-aleron-search-results]');
    this.trending = this.querySelector('[data-aleron-search-trending]');
    this.status = this.querySelector('[data-aleron-search-status]');
    this.progress = this.querySelector('[data-aleron-search-progress]');

    this.input?.addEventListener('input', this.#handleInput);

    // A trending term fills the input and searches, rather than navigating
    // away, so the shopper stays in the overlay.
    this.trending?.addEventListener('click', this.#handleTrending);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.input?.removeEventListener('input', this.#handleInput);
    this.trending?.removeEventListener('click', this.#handleTrending);
    clearTimeout(this.#timer);
    this.#controller?.abort();
  }

  onClose() {
    // Leaving stale results behind would flash them on the next open.
    clearTimeout(this.#timer);
    this.#controller?.abort();
    this.#reset();
  }

  #handleTrending = (event) => {
    const term = event.target instanceof Element ? event.target.closest('[data-term]') : null;
    if (!term || !this.input) return;

    event.preventDefault();
    this.input.value = term.dataset.term;
    this.input.focus();
    this.#search(this.input.value);
  };

  #handleInput = () => {
    const query = this.input.value.trim();

    clearTimeout(this.#timer);

    if (query.length < MIN_LENGTH) {
      this.#controller?.abort();
      this.#reset();
      return;
    }

    this.#timer = setTimeout(() => this.#search(query), DEBOUNCE_MS);
  };

  #reset() {
    if (this.results) this.results.innerHTML = '';
    this.removeAttribute('data-has-results');
    this.removeAttribute('data-busy');
    if (this.status) this.status.textContent = '';
  }

  async #search(query) {
    // Abort the in-flight request so a slow earlier response cannot land
    // after a newer one and show the wrong results.
    this.#controller?.abort();
    this.#controller = new AbortController();

    this.setAttribute('data-busy', '');

    const url = new URL(window.Theme?.routes?.predictive_search_url || '/search/suggest', window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', this.dataset.limit || '8');
    url.searchParams.set('resources[options][unavailable_products]', 'last');

    try {
      const response = await fetch(`${url.pathname}.json${url.search}`, {
        signal: this.#controller.signal,
        headers: { Accept: 'application/json' }
      });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);

      const data = await response.json();
      this.#render(data?.resources?.results?.products ?? [], query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.warn('[aleron-search]', error);
      this.#reset();
    } finally {
      this.removeAttribute('data-busy');
    }
  }

  #render(products, query) {
    if (!this.results) return;

    if (!products.length) {
      this.results.innerHTML = '';
      this.removeAttribute('data-has-results');
      if (this.status) {
        // The section passes the translated string with a literal token,
        // since Liquid cannot interpolate one inside a filter argument.
        this.status.textContent = (this.dataset.noResults || 'No results for XTERMSX').replace('XTERMSX', query);
      }
      return;
    }

    const fragment = document.createDocumentFragment();

    for (const product of products) {
      const item = document.createElement('li');
      item.className = 'aleron-search__tile';

      const link = document.createElement('a');
      link.className = 'aleron-search__tile-link';
      link.href = product.url;

      const media = document.createElement('span');
      media.className = 'aleron-search__tile-media';

      const src = product.featured_image?.url;
      if (src) {
        const image = document.createElement('img');
        // The API returns a CDN url; ask it for the size actually rendered.
        image.src = `${src}${src.includes('?') ? '&' : '?'}width=400`;
        image.alt = product.featured_image?.alt || '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.className = 'aleron-search__tile-image';
        media.appendChild(image);
      }

      const info = document.createElement('span');
      info.className = 'aleron-search__tile-info';

      const title = document.createElement('span');
      title.className = 'aleron-search__tile-title';
      // textContent, never innerHTML: these strings come from the API.
      title.textContent = product.title;

      const price = document.createElement('span');
      price.className = 'aleron-search__tile-price';
      price.textContent = product.price ? this.#money(product.price) : '';

      info.append(title, price);
      link.append(media, info);
      item.appendChild(link);
      fragment.appendChild(item);
    }

    this.results.replaceChildren(fragment);
    this.setAttribute('data-has-results', '');

    if (this.status) {
      // The section renders both plural forms with a stand-in number, so the
      // right one can be picked here without reimplementing pluralisation.
      const template =
        products.length === 1
          ? this.dataset.resultsCountOne || '1 product'
          : this.dataset.resultsCount || '99 products';
      this.status.textContent = template.replace(/\d+/, String(products.length));
    }
  }

  /**
   * Predictive search reports price as a decimal string ("1890.00"), unlike
   * the Cart API which uses cents.
   */
  #money(value) {
    return formatMoneyFromDecimal(value, this.dataset.moneyFormat);
  }
}

define('aleron-search', AleronSearch);
